import React from 'react';
import {
  Loading,
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';

type IRuleVersion = {
  number: number;
  date: string;
  description: string;
  ruleType: string;
  isTestVersion: boolean;
  isActive: boolean;
};

const RuleVersionAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IRuleVersion>>();
  const { data, isLoading } = useGetOne<AuditTrack<IRuleVersion>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IRuleVersion>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    number: resourceData.number?.hasChanged,
    date: resourceData.date?.hasChanged,
    description: resourceData.description?.hasChanged,
    ruleType: resourceData.ruleType?.hasChanged,
    isTestVersion: resourceData.isTestVersion?.hasChanged,
    isActive: resourceData.isActive?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.number && <TextDiffField source="number" label="Version" />}
          {hasFieldChanges.date && <DateDiffField source="date" label="Date" showTime />}
          {hasFieldChanges.description && <TextDiffField source="description" label="Description" />}
          {hasFieldChanges.ruleType && <TextDiffField source="ruleType" label="Rule type" />}
          {hasFieldChanges.isTestVersion && <TextDiffField source="isTestVersion" label="Test version" />}
          {hasFieldChanges.isActive && <TextDiffField source="isActive" label="Active" />}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default RuleVersionAuditDetails;
